import { useToast } from '@shared/hooks/use-toast';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PackApiAdapter } from '../adapters/PackApiAdapter';
import { PackService } from '../services/PackService';

interface UseDeletePackOptions {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
    packTitle?: string; // Para personalizar el mensaje de éxito
}

export const useDeletePack = (options: UseDeletePackOptions = {}) => {
    const queryClient = useQueryClient();
    const httpClient = createHttpClient(apiClientWH);
    const apiAdapter = new PackApiAdapter(httpClient);
    const packService = new PackService(apiAdapter);
    const { toast } = useToast();
    const { t } = useTranslation('packsModule');

    const mutation = useMutation({
        mutationFn: (id: number) => packService.deletePack(id),
        onSuccess: (_, id) => {
            // 🎯 Invalidar todas las queries relacionadas con packs
            queryClient.invalidateQueries({ queryKey: ['packs'] });
            queryClient.invalidateQueries({ queryKey: ['packs-simple'] });

            // 🎯 Remover el pack específico del cache
            queryClient.removeQueries({ queryKey: ['pack', id] });

            // 🎯 Mostrar mensaje de éxito
            const title = options.packTitle ? `"${options.packTitle}"` : "Pack";
            toast({
                title: "Pack eliminado",
                description: `El pack ${title} se ha eliminado correctamente.`,
                variant: "default",
            });

            // 🎯 Callback personalizado
            options.onSuccess?.();
        },
        onError: (error: any) => {
            // 🎯 Extraer error_code del response del backend
            const errorCode = error?.response?.data?.error?.error_code;
            
            // 🎯 Determinar el mensaje de error según el error_code
            let errorMessage = error.message || t('errors.unexpected');
            
            if (errorCode === 'PACK_IN_USE_IN_ORDERS') {
                errorMessage = t('modal.delete.error_pack_in_use');
            }
            
            // 🎯 Mostrar mensaje de error traducido
            toast({
                title: t('modal.delete.error_title'),
                description: errorMessage,
                variant: "destructive",
            });

            // 🎯 Callback personalizado de error  
            options.onError?.(error);
        },
    });

    return {
        deletePack: mutation.mutate,
        deletePackAsync: mutation.mutateAsync,
        isDeleting: mutation.isPending,
        error: mutation.error,
        isSuccess: mutation.isSuccess,
        reset: mutation.reset,
    };
}; 